// @flow
import type { IntlShape } from 'react-intl';

import { actions as licenseActions } from 'state/reducers/licenseReducer';
import { actions as authActions } from 'state/reducers/authReducer';
import type { Dispatch, GetState, System } from 'state/types';
import { getAdminSession, getExtensisSystems } from 'state/selectors';
import { SN_LENGTH, SET_SYSTEM_PANEL_ID } from 'util/constants';
import { isMainProductCode } from 'util/licenseUtils';
import logger from 'portfolio-shared/util/logging';
import { showSingleButtonAlert } from 'portfolio-shared/state/reducers/modalPromptReducer';
import localizedStrings from 'util/localizedStrings';
import { getAdminProperties } from './adminPropertiesCommands';
import { ADMIN_PROPERTIES } from 'util/AdminProperties';
import { encrypt } from 'util/serverUtils';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import { SN_TYPE_DEMO } from 'util/licenseUtils';

const {
  addLicense: addLicenseAction,
  clearIsPendingResponse,
  getExtensisAccount,
  getExtensisSystems: getExtensisSystemsAction,
  getLicensingInfo,
  setExtensisAccount: setExtensisAccountAction,
  setExtensisSystem: setExtensisSystemAction,
  setIsPendingResponse,
} = licenseActions;
const { getPublicKeyInfo } = authActions;

export function getExtensisAccountInfo() {
  return async function getExtensisAccountInfo(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const adminSessionId = getAdminSession(getState());
    dispatch(setIsPendingResponse());
    await dispatch(getExtensisAccount(adminSessionId)).catch(e => {
      logger.error(e);
      dispatch(showSingleButtonAlert(localizedStrings.LIC_ERROR_GET_ACCOUNT));
    });
    await dispatch(getLicensingInfo(adminSessionId)).catch(e => {
      logger.error(e);
    });
    dispatch(clearIsPendingResponse());
  };
}

export function addLicenses(rawText: string, intl: IntlShape) {
  return async function addLicenses(dispatch: Dispatch, getState: GetState) {
    const serialNumbers = rawText
      .trim()
      .split(/[\s]/)
      .filter(sn => sn.length >= SN_LENGTH)
      .filter(sn => sn.startsWith(SN_TYPE_DEMO))
      .sort((a, b) => {
        if (isMainProductCode(a)) {
          return -1;
        } else if (isMainProductCode(b)) {
          return 1;
        } else return 0;
      });

    dispatch(setIsPendingResponse());
    for (const sn of serialNumbers) {
      await dispatch(addLicenseAction(getAdminSession(getState()), sn)).catch(
        e => {
          logger.error(e);
          if (
            e.message &&
            e.message.includes(
              'This license is not supported by the main product license'
            )
          ) {
            dispatch(
              showSingleButtonAlert(
                localizedStrings.LIC_NOT_COMPATIBLE_WITH_LITE_ERROR_MSG
              )
            );
          } else {
            dispatch(showSingleButtonAlert(localizedStrings.LIC_ERROR_MSG));
          }
        }
      );
    }
    dispatch(clearIsPendingResponse());
    await dispatch(getAdminProperties([ADMIN_PROPERTIES.licenses]));
  };
}

export function addLicense(serialNumber: string) {
  return async function addLicense(dispatch: Dispatch, getState: GetState) {
    await dispatch(addLicenseAction(getAdminSession(getState()), serialNumber));
  };
}

export function setExtensisAccount(account: string, password: string) {
  return async function setExtensisAccount(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getAdminSession(getState());
    if (session) {
      const publicKeyInfoResult = await dispatch(getPublicKeyInfo());
      const encryptedPassword = encrypt(password, publicKeyInfoResult.payload);
      dispatch(setIsPendingResponse());
      await dispatch(
        setExtensisAccountAction(session, account, encryptedPassword)
      ).catch(e => {
        logger.error(e);
        dispatch(clearIsPendingResponse());
        dispatch(showSingleButtonAlert(localizedStrings.LIC_SET_ACCOUNT_ERROR));
        return;
      });

      // if setting account, get the systems so the admin can select one
      if (account) {
        await dispatch(getExtensisSystemsAction(session));
        const systems: System[] = getExtensisSystems(getState());
        // if more than one system, make user select
        if (systems.length > 1) {
          dispatch(clearIsPendingResponse());
          dispatch(showDialogPanel(SET_SYSTEM_PANEL_ID));
          return;
        }
      }

      // no error and no system selection needed
      dispatch(clearIsPendingResponse());
      await dispatch(getExtensisAccountInfo());
      await dispatch(getAdminProperties([ADMIN_PROPERTIES.licenses]));
    }
  };
}

export function setExtensisSystem(systemId: string) {
  return async function setExtensisSystem(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const session = getAdminSession(getState());
    if (session) {
      dispatch(setIsPendingResponse());
      await dispatch(setExtensisSystemAction(session, systemId));
      await dispatch(getExtensisAccountInfo());
      dispatch(clearIsPendingResponse());
      await dispatch(getAdminProperties([ADMIN_PROPERTIES.licenses]));
    }
  };
}



// WEBPACK FOOTER //
// ./src/state/commands/licenseCommands.js